import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useLocaleFormatter } from "@/lib/i18n/useLocaleFormatter";
import { submitRetailPrepBatch } from "@/lib/api/retail";
import type {
  RetailInventoryLevel,
  InventoryClassificationSnapshot,
} from "@/features/retail/types/datasets";

type KitchenPrepDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  level: RetailInventoryLevel | null;
  classification: InventoryClassificationSnapshot | null;
  productName: string | null;
  locationName: string | null;
  onSuccess?: () => void;
};

const DEFAULT_QUANTITY = "5";

const KitchenPrepDialog = ({
  open,
  onOpenChange,
  level,
  classification,
  productName,
  locationName,
  onSuccess,
}: KitchenPrepDialogProps) => {
  const { t } = useTranslation(["common", "retail"]);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { formatNumber } = useLocaleFormatter();

  const [quantity, setQuantity] = useState(DEFAULT_QUANTITY);
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [recipeId, setRecipeId] = useState("");

  useEffect(() => {
    if (!open) {
      setQuantity(DEFAULT_QUANTITY);
      setReference("");
      setNote("");
      setRecipeId("");
    }
  }, [open]);

  const unitLabel = useMemo(() => {
    if (!classification) {
      return "ea";
    }
    return (
      classification.displayUnit?.abbreviation ??
      classification.unit?.abbreviation ??
      classification.unitId
    );
  }, [classification]);

  const mutation = useMutation({
    mutationFn: submitRetailPrepBatch,
    onSuccess: () => {
      toast({
        title: t("retail.inventory.kitchen.toast.prep", { defaultValue: "Prep batch recorded." }),
      });
      queryClient.invalidateQueries({ queryKey: ["retail", "inventory"] });
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      toast({
        title: t("retail.inventory.toast.error", { defaultValue: "Inventory update failed" }),
        description:
          error instanceof Error
            ? error.message
            : t("status.tryAgain", { defaultValue: "Please try again." }),
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!level) {
      toast({
        title: t("retail.inventory.toast.error", { defaultValue: "Inventory update failed" }),
        description: t("retail.inventory.toast.missingLevelDescription", {
          defaultValue: "Refresh and try again.",
        }),
        variant: "destructive",
      });
      return;
    }
    const parsedQuantity = Number.parseFloat(quantity);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      toast({
        title: t("retail.quickActions.receive.invalidQuantityTitle", {
          defaultValue: "Invalid quantity",
        }),
        description: t("retail.quickActions.receive.invalidQuantityDescription", {
          defaultValue: "Enter a positive number of units.",
        }),
        variant: "destructive",
      });
      return;
    }

    mutation.mutate({
      levelId: level.id,
      quantity: parsedQuantity,
      reference: reference.trim() ? reference.trim() : null,
      note: note.trim() ? note.trim() : null,
      recipeId: recipeId.trim() ? recipeId.trim() : null,
    });
  };

  const unitHint = classification
    ? t("retail.inventory.kitchen.dialogs.prep.unitHint", {
        defaultValue: "Measured in {{unit}} units.",
        unit: unitLabel,
      })
    : null;

  const availableLabel = level
    ? t("retail.inventory.kitchen.dialogs.prep.available", {
        defaultValue: "{{count}} on hand",
        count: formatNumber(Math.max(0, (level.onHand ?? 0) - (level.allocated ?? 0))),
      })
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t("retail.inventory.kitchen.dialogs.prep.title", { defaultValue: "Log prep batch" })}
          </DialogTitle>
          <DialogDescription>
            {t("retail.inventory.kitchen.dialogs.prep.description", {
              defaultValue: "Record finished prep and link it to a recipe or station.",
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-5">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {productName ?? t("retail.inventory.table.notAvailable", { defaultValue: "-" })}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {locationName ??
                t("retail.receiving.unknownLocation", { defaultValue: "Unassigned location" })}
            </span>
            {availableLabel ? (
              <span className="text-xs text-slate-500 dark:text-slate-400">{availableLabel}</span>
            ) : null}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="kitchen-prep-quantity">
              {t("retail.inventory.kitchen.dialogs.prep.quantityLabel", { defaultValue: "Quantity prepared" })}
            </Label>
            <Input
              id="kitchen-prep-quantity"
              inputMode="decimal"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              placeholder="10"
              disabled={mutation.isPending}
            />
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {unitHint ?? t("retail.inventory.kitchen.dialogs.prep.quantityHint", { defaultValue: "Defaults to base units." })}
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="kitchen-prep-reference">
              {t("retail.inventory.kitchen.dialogs.prep.referenceLabel", { defaultValue: "Reference (optional)" })}
            </Label>
            <Input
              id="kitchen-prep-reference"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              placeholder="Batch ticket"
              disabled={mutation.isPending}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="kitchen-prep-recipe">
              {t("retail.inventory.kitchen.dialogs.prep.recipeLabel", { defaultValue: "Recipe ID (optional)" })}
            </Label>
            <Input
              id="kitchen-prep-recipe"
              value={recipeId}
              onChange={(event) => setRecipeId(event.target.value)}
              placeholder="recipe-123"
              disabled={mutation.isPending}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="kitchen-prep-note">
              {t("retail.inventory.kitchen.dialogs.prep.noteLabel", { defaultValue: "Notes (optional)" })}
            </Label>
            <Textarea
              id="kitchen-prep-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              disabled={mutation.isPending}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            {t("common.actions.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending}>
            {mutation.isPending
              ? t("retail.inventory.kitchen.dialogs.prep.submitPending", { defaultValue: "Recording…" })
              : t("retail.inventory.kitchen.dialogs.prep.submitLabel", { defaultValue: "Save prep batch" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KitchenPrepDialog;
